import { LvcItemSourceData } from '../../types/global'
import { LvcRecommendation } from '../componentsV2/lvc-card/lvc-recommended'
import { config } from '../constants/config'
import { formatEventDateTime, formatStartTime } from './date'
import { Locale } from './locale'
import { ConsoleError } from './logging'
import MainModule from './main'
import { LvcPrimarySliderItem } from './sliderUtils'
import { TabAccountant } from './tabAccountant'

export const PASSWORD_REGEX = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w\s]).{8,}$/
export const EMAIL_REGEX = /^[\w.%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
export interface Attribute {
  name: string
  value: string
}

export const isMobile = () => {
  return window.matchMedia('(max-width: 991px)').matches
}

export function isElementVisible(el: HTMLElement): boolean {
  const style = window.getComputedStyle(el)
  if (style.display === 'none') return false

  if (style.visibility === 'hidden') return false

  let cbr = el.getBoundingClientRect()

  if (el.offsetWidth + el.offsetHeight + cbr.width + cbr.height === 0) return false

  if (el.parentElement && !isElementVisible(el.parentElement)) return false

  return true
}

export function hasFeature(key: string): boolean {
  return (config?.featureFlags as any)?.[key] !== false
}

export function generateId(): string {
  let id: string[] = []

  for (let i = 0; i < 4; i++) {
    id.push(Math.round(Math.random() * 100000000).toString(16))
  }

  return id.join('-')
}

export function sanitizeFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.')
  const base = lastDot > 0 ? fileName.slice(0, lastDot) : fileName
  const extension = lastDot > 0 ? fileName.slice(lastDot + 1) : ''

  // strips accents (NFD-decomposes then drops combining marks) before
  // replacing anything else unsafe — keeps GCS signed URLs valid, which
  // break on accented filenames (LVC-406)
  const sanitize = (value: string) =>
    value
      .normalize('NFD')
      .replace(/\p{M}/gu, '')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')

  const safeBase = sanitize(base) || 'file'
  const safeExtension = sanitize(extension)

  return safeExtension ? `${safeBase}.${safeExtension}` : safeBase
}

export function cloneObject(obj: any): any {
  if (typeof window !== 'undefined' && typeof (window as any).structuredClone === 'function') {
    return (window as any).structuredClone(obj)
  }

  return JSON.parse(JSON.stringify(obj))
}

// Andrei: I deemed unnecessary to install lodash just to have the escape and unescape functions, so I implemented them here
const htmlEscapes = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
}
const reUnescapedHtml = /[&<>"']/g
const reHasUnescapedHtml = new RegExp(reUnescapedHtml.source)

export function escapeHtml(str: string): string {
  return str && reHasUnescapedHtml.test(str)
    ? str.replace(reUnescapedHtml, chr => (htmlEscapes as any)[chr])
    : str || ''
}

const htmlUnescapes = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
}

const reEscapedHtml = /&(?:amp|lt|gt|quot|#(0+)?39);/g
const reHasEscapedHtml = RegExp(reEscapedHtml.source)

export function unescapeHtml(str: string): string {
  return str && reHasEscapedHtml.test(str)
    ? str.replace(reEscapedHtml, entity => (htmlUnescapes as any)[entity] || "'")
    : str || ''
}

export function inlineStyle(str: string): string {
  try {
    return btoa(str)
  } catch (e) {
    return ''
  }
}
export function parseInlineStyle(str: string): string {
  try {
    return atob(str)
  } catch (e) {
    return ''
  }
}

export function canShowOnThisTab(): boolean {
  if (document.hasFocus()) {
    return true
  }

  if (TabAccountant.getInstance().isOnlyTab()) {
    return true
  }

  return false
}

export function populateObject(
  template: any,
  data?: any,
  defaultKeys?: string[],
  defaultValue?: any
): any {
  let obj: any

  if (typeof structuredClone === 'function') {
    obj = structuredClone(template)
  } else {
    obj = JSON.parse(JSON.stringify(template))
  }

  if (defaultKeys && defaultKeys.length > 0 && defaultValue !== undefined) {
    for (let i = 0; i < defaultKeys.length; i++) {
      if (!obj.hasOwnProperty(defaultKeys[i])) {
        obj[defaultKeys[i]] = defaultValue
      }
    }
  }
  if (data) {
    for (let key in data) {
      obj[key] = data[key]
    }
  }
  return obj
}

export function replaceTemplate(
  template: string,
  data: { [key: string]: string },
  allowEmpty?: boolean
): string {
  return template.replace(/{{{\s*(\w+)\s*}}}/g, (_, key) => {
    if (data.hasOwnProperty(key)) {
      return data[key]
    } else {
      if (allowEmpty) {
        return '{{{' + key + '}}}'
      } else {
        return ''
      }
    }
  })
}

export function copyTextToClipboard(text: string): void {
  const textarea = document.createElement('textarea')
  textarea.value = text
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

export interface MaskOptions {
  mask: string
  patternsMap?: { [key: string]: RegExp }
}

function extractInputCharacters(rawInput: string, patternsMap: { [key: string]: RegExp }): string {
  let validCharacters = ''
  for (let i = 0; i < rawInput.length; i++) {
    const currentChar = rawInput[i]
    // Check if this character matches any of our patterns
    for (const charPattern of Object.values(patternsMap)) {
      // Skip the wildcard pattern (/./) to avoid matching separators
      if (charPattern.source === '.') continue

      if (new RegExp(charPattern).test(currentChar)) {
        validCharacters += currentChar
        break
      }
    }
  }
  return validCharacters
}

export function applyMask(rawInput: string, options: MaskOptions) {
  const { mask = '', patternsMap = {} } = options

  // First, extract only the valid input characters (remove separators)
  // Example: "123-4" becomes "1234", "H1C 2" becomes "H1C2"
  const cleanInput = extractInputCharacters(rawInput, patternsMap)

  let formattedOutput = ''
  let inputPosition = 0 // Tracks our position in the cleaned input

  // Iterate through each character in the mask pattern
  for (let maskPosition = 0; maskPosition < mask.length; maskPosition++) {
    const maskChar = mask[maskPosition]
    const charPattern = patternsMap[maskChar]

    if (charPattern) {
      // This is a pattern character (e.g., '#' for digit, 'A' for letter)
      // Try to match the next character from cleanInput against this pattern
      const regex = new RegExp(charPattern)
      const match = regex.exec(cleanInput.slice(inputPosition))

      if (match) {
        // Match found - add the matched character to output
        formattedOutput += match[0]
        inputPosition += match[0].length
      } else {
        // No match - we've run out of valid input, stop processing
        break
      }
    } else {
      // This is a separator character (e.g., '-', ' ', '(', ')')
      // Add it directly to the output without consuming any input
      formattedOutput += maskChar
      // Don't increment inputPosition for separator characters
    }
  }

  return formattedOutput
}

export function mapEvent(item: LvcItemSourceData): LvcPrimarySliderItem {
  // Take the list of recommendations, look for any matches by objectID
  // If match, append recommendation data to mapped item.
  const recommendedContentList = MainModule.getInstance().getRecommendedContent()
  let recommendedData = {
    recommendedBy: [] as LvcRecommendation[],
  }
  if (recommendedContentList && recommendedContentList.length > 0) {
    const matched = recommendedContentList
      .filter(recommendation => recommendation.objectID === item.objectID)
      .slice(0, 3) // Max 3
    for (const item of matched) {
      recommendedData.recommendedBy.push({
        name: item.name,
        image: item.image,
      })
    }
  }
  let imageUrl = ''
  try {
    if (item.medias) {
      let parsed = JSON.parse(item?.medias?.[0]?.media ?? '') ?? [{ url: '' }]
      imageUrl = parsed?.[0]?.url ?? item?.image?.url ?? ''
    } else {
      imageUrl = item?.image?.url ?? ''
    }
  } catch (e) {
    ConsoleError(true, 'media failed to parse for ', item?.urlConstruct, item?.medias)
  }
  let exhibitionType =
    item?.table === 'exhibitionEvents' || item?.schema === 'exhibitionEvents'
      ? item?.type
      : undefined
  let mapped: any = {
    ...recommendedData,
    objectID: item?.objectID,
    name: {
      fr: item.nameFr || (item.name?.[Locale.language] as string),
      en: item.nameEn || (item.name?.[Locale.language] as string),
    },
    schema: item.table || (item.schema as string),
    id: item.id as string,
    exhibitionType: exhibitionType,
    location: compileLocationLabel(item).location,
    city: compileLocationLabel(item).city,
    locationType: item.placeLocationType,
    artist: {
      fr:
        cleanAndSortArtists(item.artists)?.nameFr ||
        (cleanAndSortArtists(item.artists)?.name?.[Locale.language] as string),
      en:
        cleanAndSortArtists(item.artists)?.nameEn ||
        (cleanAndSortArtists(item.artists)?.name?.[Locale.language] as string),
    },
    urlConstruct: item?.urlConstruct ?? { en: '', fr: '' },
    cardImage: imageUrl,
    startDate: item.startDate,
    endDate: item.endDate,
    startDay: item.startDay,
    status: item.status,
    formattedDateTime: formatEventDateTime(
      Locale.language,
      { includeStartTime: true },
      item?.startDate,
      item?.endDate
    ),
    startTime: formatStartTime(Locale.language, item?.startDate, item?.endDate),
    uiAdditionalTypes: item?.uiAdditionalTypes,
    isAccessibleForFree: item?.isAccessibleForFree,
    url: {
      fr: item.urlFr,
      en: item.urlEn,
    },
    festivals: item.festivals,
    sameDayOffers: item.sameDayOffers,
    publishedEventsCount: item.publishedEventsCount,
  }
  return mapped
}

export function compileLocationLabel(data: LvcItemSourceData) {
  if (data.location)
    return {
      location: data.location,
    }
  const placeName = {
    fr: data?.placeNameFr,
    en: data?.placeNameEn,
  }
  const streetAddress = data?.streetAddress
  const city = data?.city || data?.addressLocality
  const province = data?.addressRegion || data?.province
  const isArea =
    data.locationType === 'AdministrativeArea' || data.placeLocationType === 'AdministrativeArea'
  // format for place results
  if (data.table === 'places') {
    const location = isArea
      ? [city, province].filter(Boolean).join(', ')
      : [streetAddress, city, province].filter(Boolean).join(', ')
    return {
      location: {
        fr: location,
        en: location,
      },
      city: {
        fr: null,
        en: null,
      },
    }
  } else {
    const locationFr = isArea
      ? [placeName.fr, province].filter(item => item).join(', ')
      : [placeName.fr].filter(item => item).join(', ')
    const locationEn = isArea
      ? [placeName.en, province].filter(item => item).join(', ')
      : [placeName.en].filter(item => item).join(', ')
    return {
      location: {
        fr: locationFr,
        en: locationEn,
      },
      city: {
        fr: isArea ? null : city,
        en: isArea ? null : city,
      },
    }
  }
}

export function cleanAndSortArtists(artists: any[]) {
  if (!artists) return
  const artisteList = artists.filter(artist => artist?.contributionType === 'Artiste')
  const organisateurList = artists.filter(artist => artist?.contributionType === 'Organisateur')
  if (artisteList.length === 0 && organisateurList.length === 0) return
  if (artisteList.length > 0) {
    return sortBySequenceNumber(artisteList)?.[0] || null
  } else if (organisateurList.length > 0) {
    return sortBySequenceNumber(organisateurList)?.[0] || null
  } else {
    return
  }
}

export const sortBySequenceNumber = (array: any[]) => {
  if (array && array.length > 0) {
    array.sort((a, b) => {
      if (a.sequenceNumber === null) {
        return 1
      }
      if (b.sequenceNumber === null) {
        return -1
      }
      if (a.sequenceNumber === b.sequenceNumber) {
        return 0
      }
      return a.sequenceNumber < b.sequenceNumber ? -1 : 1
    })
    return array
  } else {
    return null
  }
}

export function getCardPlaceholder(itemType: string) {
  const imagesPrefix = 'https://cloud.squidex.io/api/assets/lvc-dev'
  const organizationImage = `${imagesPrefix}/3b13b632-c681-4ea8-82b9-29fd0bbf2998/placeholder-organisation.webp?version=0`
  const artistImage = `${imagesPrefix}/eafd16b0-8e57-40e8-9edd-0662193861e4/placeholder-artist.webp?version=0`
  const eventSeriesImage = `${imagesPrefix}/558e0309-c101-47a2-8b2c-ca4275009f3e/placeholder-serie.webp?version=0`
  const eventImage = `${imagesPrefix}/d6da24cd-2549-481b-98e5-fa3548c469a0/placeholder-evenement.webp?version=0`
  const exhibitionImage = `${imagesPrefix}/d681f836-b6c3-418e-aa7c-dcdb26b72473/placeholder-exposition.webp?version=0`
  const placeImage = `${imagesPrefix}/ee54196e-12bc-417b-b4fc-94b28f14ad11/placeholder-lieu.webp?version=0`
  switch (itemType?.toLowerCase()) {
    case 'artist':
    case 'artists':
    case 'person': {
      return artistImage
    }
    case 'organization':
    case 'organizations': {
      return organizationImage
    }
    case 'eventseries': {
      return eventSeriesImage
    }
    case 'eventoffers':
    case 'eventoffer':
    case 'event':
    case 'events': {
      return eventImage
    }
    case 'exhibitionevent':
    case 'exhibitionevents':
    case 'exhibitionEvents': {
      return exhibitionImage
    }
    case 'places':
    case 'place': {
      return placeImage
    }
    default: {
      return eventImage
    }
  }
}

export const convertUnixTimestamp = (unixTimestamp: number, displayTime?: boolean) => {
  const date = new Date(unixTimestamp)
  const day = date.getDate()
  const month = Locale.getKey('general.months.short.' + (date.getMonth() + 1))
  const year = date.getFullYear()
  const hours = ('0' + date.getHours()).slice(-2)
  const minutes = ('0' + date.getMinutes()).slice(-2)
  if (displayTime === false) {
    return `${day} ${month} ${year}`
  } else {
    return `${day} ${month} ${year} - ${hours}:${minutes}`
  }
}

export const delay = (ms: number) => new Promise(res => setTimeout(res, ms))

export const getVisibleWidth = (element?: HTMLElement) => {
  if (!element) {
    return window.innerWidth > 0 ? window.innerWidth : screen.width
  }

  return element.offsetWidth
}

export const getVisibleHeight = (element?: HTMLElement) => {
  if (!element) {
    return window.innerHeight > 0 ? window.innerHeight : screen.height
  }

  return element.offsetHeight
}

export const recursiveCleanObject = (obj: any) => {
  for (let key in obj) {
    if (obj[key] === undefined || obj[key] === null) {
      delete obj[key]
    } else if (typeof obj[key] === 'object') {
      recursiveCleanObject(obj[key])
    }
  }
  return obj
}

export const isObjectEmpty = (object: object) => {
  return !object || (object && Object.keys(object).length === 0)
}

export const unescapeHtmlProSpaceData = <T>(obj: T): T => {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(item => unescapeHtmlProSpaceData(item)) as T
  }

  const result: any = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = unescapeHtml(value)
    } else if (typeof value === 'object' && value !== null) {
      result[key] = unescapeHtmlProSpaceData(value)
    } else {
      result[key] = value
    }
  }

  return result as T
}
